import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, Clock, FileText, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeIcons = {
  success: CheckCheck,
  warning: AlertTriangle,
  action: Clock,
  info: Info,
};

const typeColors = {
  success: 'text-[var(--success)]',
  warning: 'text-[var(--warning)]',
  action: 'text-[var(--primary-400)]',
  info: 'text-[var(--info)]',
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);
  
  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };
  
  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    pollRef.current = setInterval(fetchNotifications, 60000);
    return () => clearInterval(pollRef.current);
  }, []);
  
  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };
  
  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/read/${id}`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking read:', error);
    }
  };
  
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "A l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold"
              style={{ backgroundColor: 'var(--error)' }}
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        className="w-80 p-0 bg-[var(--bg-primary)]"
        style={{ backgroundColor: 'var(--bg-primary)' }}
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between p-3 border-b border-[var(--neutral-200)]">
          <h3 className="font-semibold text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
            >
              <Check className="h-3 w-3 mr-1" />
              Tout lire
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="h-8 w-8 mx-auto text-[var(--text-tertiary)] mb-2" />
              <p className="text-sm text-[var(--text-secondary)]">Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--neutral-100)]">
              {notifications.slice(0, 20).map((notif) => {
                const Icon = typeIcons[notif.type] || Info;
                const colorClass = typeColors[notif.type] || 'text-[var(--text-secondary)]';
                
                return (
                  <div 
                    key={notif.id}
                    className={cn(
                      "p-3 hover:bg-[var(--bg-secondary)] transition-all duration-150 cursor-pointer",
                      !notif.is_read && "bg-[var(--primary-50)]"
                    )}
                    onClick={() => !notif.is_read && handleMarkRead(notif.id)}
                    data-testid="notification-item"
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon className={cn("h-4 w-4", colorClass)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm", !notif.is_read ? "font-semibold" : "font-medium")}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-xs text-[var(--text-tertiary)] mt-1">
                          {formatDate(notif.created_at)}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--primary-400)' }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
